import type { ReactNode } from "react";

import {
  BarChart3,
  Building2,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  ShieldCheck,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";

/**
 * The 256px sidebar from mockups/shell.js, as React.
 *
 * The structure is the validated one: MAIN, then REPORTING showing only what this user is
 * granted, then ADMINISTRATION for administrators only.
 *
 * ⚠ Hiding a nav item is presentation, never protection. Every route behind these links is
 * separately gated by ProtectedRoute/AdminRoute, and every one of them is gated again at
 * the API. A link that is merely invisible is a link anybody can still type.
 */

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
  soon?: boolean;
}

function Section({ label }: { label: string }) {
  return (
    <div className="mb-2 mt-6 px-3">
      <p className="eyebrow px-3">{label}</p>
    </div>
  );
}

function Item({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          `nav-item flex items-center gap-3 rounded-l-lg px-3 py-2.5 text-sm font-medium ${
            isActive ? "nav-item-active" : "text-slate-600"
          }`
        }
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="truncate">{item.label}</span>
        {item.badge ? (
          <span className="ml-auto rounded-full bg-brand-rose px-1.5 py-0.5 text-[10px] font-bold text-white">
            {item.badge}
          </span>
        ) : item.soon ? (
          <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-400">
            SOON
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}

export function Sidebar({
  open,
  onClose,
  reports,
}: {
  open: boolean;
  onClose: () => void;
  reports: { key: string; shortName: string; isReady: boolean }[];
}) {
  const { user, isAdmin, logout } = useAuth();
  if (!user) return null;

  const initials =
    user.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || user.email[0]?.toUpperCase() || "?";

  const reportIcon = (key: string): LucideIcon => (key === "quality-audits" ? ClipboardCheck : BarChart3);

  return (
    <>
      <aside
        id="cs-sidebar"
        className={`fixed bottom-0 left-0 top-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 shrink-0 items-center border-b border-slate-100 px-6">
          <div className="flex items-center gap-3">
            <div className="gradient-band flex h-8 w-8 items-center justify-center rounded-lg text-white">
              <BarChart3 className="h-4 w-4" />
            </div>
            <span className="gradient-text text-lg font-bold">Quality Field</span>
          </div>
        </div>

        <nav className="custom-scrollbar flex-1 overflow-y-auto py-4">
          <div className="mb-2 px-3">
            <p className="eyebrow px-3">Main</p>
          </div>
          <ul className="space-y-0.5 px-3">
            <Item item={{ to: "/", icon: LayoutDashboard, label: "Reports" }} />
          </ul>

          {reports.length > 0 && (
            <>
              <Section label="Reporting" />
              <ul className="space-y-0.5 px-3">
                {reports.map((r) => (
                  <Item
                    key={r.key}
                    item={{
                      to: `/reports/${r.key}`,
                      icon: reportIcon(r.key),
                      label: r.shortName,
                      soon: !r.isReady,
                    }}
                  />
                ))}
              </ul>
            </>
          )}

          {isAdmin && (
            <>
              <Section label="Administration" />
              <ul className="space-y-0.5 px-3">
                <Item item={{ to: "/admin/users", icon: Users, label: "Users" }} />
                <Item item={{ to: "/admin/access", icon: ShieldCheck, label: "Report Access" }} />
                <Item item={{ to: "/admin/companies", icon: Building2, label: "Companies" }} />
              </ul>
            </>
          )}
        </nav>

        <div className="shrink-0 border-t border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="gradient-band flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-800">{user.displayName}</p>
              <p className="truncate text-xs text-slate-400">{user.email}</p>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              title="Sign out"
              aria-label="Sign out"
              className="text-slate-400 hover:text-slate-600"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {open && (
        <div
          id="cs-backdrop"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-hidden="true"
        />
      )}
    </>
  );
}

export function SidebarLayout({ children }: { children: ReactNode }) {
  return <div className="min-h-screen bg-slate-50 lg:pl-64">{children}</div>;
}
